export type RelationshipTreeDirection = 'child' | 'parent';

/**
 * Encodes a relationship tree row ID that includes the full ancestry path.
 *
 * Format: "{parentTreeRowId}/{docId}:{direction}" for nested rows, or just "{docId}" for root rows.
 * Using "/" as the path separator and ":" for the direction suffix.
 *
 * Examples:
 * - Root document: "docA"
 * - Child of root docA: "docA/docB:child"
 * - Parent of root docA: "docA/docC:parent"
 * - Child of nested docB (which is under docA): "docA/docB:child/docD:child"
 *
 * This ensures each position in the tree has a unique ID, so the same document
 * appearing at multiple levels won't interfere with each other's expand/collapse state.
 */
export function encodeRelationshipTreeRowId(args: {
  documentId: string;
  direction?: RelationshipTreeDirection | null;
  /** The full tree row ID of the parent row (not just the document ID) */
  parentTreeRowId?: string | null;
}): string {
  const { direction, documentId, parentTreeRowId } = args;
  if (parentTreeRowId && direction) return `${parentTreeRowId}/${documentId}:${direction}`;

  return documentId;
}

/**
 * Decodes the document ID from a relationship tree row ID.
 * Returns the document ID of the row itself (the last segment).
 */
export function decodeDocumentIdFromRelationshipTreeRowId(rowId: string): string {
  const segments = rowId.split('/');
  const lastSegment = segments[segments.length - 1];
  const colonIndex = lastSegment.indexOf(':');
  if (colonIndex !== -1) return lastSegment.substring(0, colonIndex);

  return lastSegment;
}

/**
 * Decodes the parent tree row ID from a relationship tree row ID.
 * This is the full path to the parent row.
 * Returns null for root rows.
 */
export function decodeParentTreeRowIdFromRelationshipTreeRowId(rowId: string): string | null {
  const lastSlashIndex = rowId.lastIndexOf('/');
  if (lastSlashIndex === -1) return null;
  return rowId.substring(0, lastSlashIndex);
}

/**
 * Extracts all document IDs from a tree row ID path.
 *
 * Given "docA/docB:child/docC:parent", returns ["docA", "docB", "docC"].
 * Used to detect circular references when expanding tree nodes.
 */
export function extractAllDocumentIdsFromTreeRowId(rowId: string): string[] {
  return rowId.split('/').map(segment => {
    const colonIndex = segment.indexOf(':');
    return colonIndex !== -1 ? segment.substring(0, colonIndex) : segment;
  });
}

/**
 * Checks if a row ID represents a root level document.
 */
export function isRootLevelRowId(rowId: string): boolean {
  return !rowId.includes('/');
}
